/**
 * Sign-in UI controller. Wires the markup in taskpane.html to lib/auth.ts.
 *
 * State flow:
 *   loading       -> spinner / hide everything
 *   signed_out    -> email form visible, code form hidden
 *   code_sent     -> code form visible, email form hidden
 *   signed_in     -> hide sign-in panel, reveal app shell, set email + sign-out
 */
import {
  getAuthState,
  initAuth,
  isDevMode,
  onAuthStateChange,
  requestMagicCode,
  signOut,
  verifyMagicCode,
} from "../lib/auth";
import type { AuthState } from "../lib/auth";

interface SignInElements {
  panel: HTMLElement;
  appShell: HTMLElement;
  emailForm: HTMLFormElement;
  emailInput: HTMLInputElement;
  emailSubmit: HTMLButtonElement;
  codeForm: HTMLFormElement;
  codeInput: HTMLInputElement;
  codeSubmit: HTMLButtonElement;
  codeBackBtn: HTMLButtonElement;
  codeEmail: HTMLElement;
  error: HTMLElement;
  modeNote: HTMLElement;
  emailDisplay: HTMLElement;
  signOutBtn: HTMLButtonElement;
}

function $(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`signIn: missing element #${id}`);
  return el;
}

function collect(): SignInElements {
  return {
    panel: $("signin-panel"),
    appShell: $("app-shell"),
    emailForm: $("signin-email-form") as HTMLFormElement,
    emailInput: $("signin-email") as HTMLInputElement,
    emailSubmit: $("signin-email-submit") as HTMLButtonElement,
    codeForm: $("signin-code-form") as HTMLFormElement,
    codeInput: $("signin-code") as HTMLInputElement,
    codeSubmit: $("signin-code-submit") as HTMLButtonElement,
    codeBackBtn: $("signin-back") as HTMLButtonElement,
    codeEmail: $("signin-code-email"),
    error: $("signin-error"),
    modeNote: $("signin-mode"),
    emailDisplay: $("auth-email"),
    signOutBtn: $("sign-out-btn") as HTMLButtonElement,
  };
}

function showError(els: SignInElements, msg: string): void {
  els.error.textContent = msg;
  els.error.hidden = false;
}

function clearError(els: SignInElements): void {
  els.error.textContent = "";
  els.error.hidden = true;
}

function render(els: SignInElements, state: AuthState): void {
  switch (state.kind) {
    case "loading":
      els.panel.hidden = true;
      els.appShell.hidden = true;
      break;
    case "signed_out":
      els.panel.hidden = false;
      els.appShell.hidden = true;
      els.emailForm.hidden = false;
      els.codeForm.hidden = true;
      els.emailInput.disabled = false;
      els.emailSubmit.disabled = false;
      els.emailDisplay.textContent = "";
      els.signOutBtn.hidden = true;
      break;
    case "code_sent":
      els.panel.hidden = false;
      els.appShell.hidden = true;
      els.emailForm.hidden = true;
      els.codeForm.hidden = false;
      els.codeEmail.textContent = state.email;
      els.codeInput.disabled = false;
      els.codeSubmit.disabled = false;
      els.codeInput.value = "";
      // Defer the focus call to after the form unhide takes effect.
      setTimeout(() => els.codeInput.focus(), 0);
      break;
    case "signed_in":
      els.panel.hidden = true;
      els.appShell.hidden = false;
      els.emailDisplay.textContent = state.email;
      els.signOutBtn.hidden = false;
      break;
  }
}

/**
 * Initialize the sign-in UI. Returns a promise that resolves once the
 * user is signed in for the first time (or immediately if they already
 * have a valid session). The rest of the task pane bootstrap should
 * await this so the app shell only initializes after auth.
 */
export async function initSignInUI(): Promise<void> {
  const els = collect();

  if (isDevMode()) {
    els.modeNote.hidden = false;
    els.modeNote.textContent =
      "Dev mode — no Clerk publishable key set. Any email + any 6-digit code work.";
  }

  els.emailForm.addEventListener("submit", async (e) => {
    e.preventDefault();
    clearError(els);
    const email = els.emailInput.value.trim();
    if (!email) {
      showError(els, "Email required.");
      return;
    }
    els.emailInput.disabled = true;
    els.emailSubmit.disabled = true;
    try {
      await requestMagicCode(email);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showError(els, `Couldn't send code: ${msg}`);
      els.emailInput.disabled = false;
      els.emailSubmit.disabled = false;
    }
  });

  els.codeForm.addEventListener("submit", async (e) => {
    e.preventDefault();
    clearError(els);
    const code = els.codeInput.value.trim();
    if (!/^[0-9]{6}$/.test(code)) {
      showError(els, "Code must be 6 digits.");
      return;
    }
    els.codeInput.disabled = true;
    els.codeSubmit.disabled = true;
    try {
      await verifyMagicCode(code);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showError(els, `Code rejected: ${msg}`);
      els.codeInput.disabled = false;
      els.codeSubmit.disabled = false;
    }
  });

  els.codeBackBtn.addEventListener("click", () => {
    clearError(els);
    // Returning to the email form means abandoning the in-flight signIn
    // attempt. On the Clerk side that's fine — the next requestMagicCode
    // creates a fresh signIn object.
    const cur = getAuthState();
    if (cur.kind === "code_sent") {
      // Force the UI back to signed_out; auth.ts treats the code form
      // as a transient UI state, not an auth-state milestone.
      render(els, { kind: "signed_out" });
    }
  });

  els.signOutBtn.addEventListener("click", async () => {
    try {
      await signOut();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showError(els, `Sign-out failed: ${msg}`);
    }
  });

  // Subscribe to auth state changes (handles initial render too).
  onAuthStateChange((state) => render(els, state));

  // Kick off the initial auth check.
  await initAuth();

  // Resolve when signed in. Subsequent sign-outs don't reject — the
  // caller's app-shell init runs once and the sign-in panel takes over
  // again when state flips to signed_out.
  if (getAuthState().kind !== "signed_in") {
    await new Promise<void>((resolve) => {
      const unsubscribe = onAuthStateChange((s) => {
        if (s.kind === "signed_in") {
          unsubscribe();
          resolve();
        }
      });
    });
  }
}
